import type { EventPayload, OperationalEventType, Outcome, ProjectStage } from '../api/types';

export type EventField = 'supportId' | 'actionId' | 'windowStart' | 'windowEnd' | 'outcome' | 'stage' | 'artifactUrl' | 'minutes' | 'note';
export type ReferenceField = 'supportId' | 'actionId';

export interface EventSpec {
  label: string;
  description: string;
  /** Fields the strict API schema requires, in display order. */
  required: EventField[];
  optional: EventField[];
  needsProject: boolean;
  stewardOnly: boolean;
  /** The chain reference. Never typed by people: generated for a root, picked from existing roots otherwise. */
  reference?: ReferenceField;
  /** True for the event that starts a chain (a request or an action). */
  root?: boolean;
}

/** Mirrors packages/measurement EventPayloads. The API rejects unknown payload keys, so only these are sent. */
export const eventSpecs: Record<OperationalEventType, EventSpec> = {
  measurement_started: {
    label: 'Start measurement',
    description: 'Records the explicit start of prospective measurement. Nothing before this point counts as zero.',
    required: ['note'],
    optional: [],
    needsProject: false,
    stewardOnly: true,
  },
  support_requested: {
    label: 'Support requested',
    description: 'An explicit request for help. A request does not oblige anyone to respond.',
    required: ['supportId', 'note'],
    optional: [],
    needsProject: false,
    stewardOnly: false,
    reference: 'supportId',
    root: true,
  },
  support_window_accepted: {
    label: 'Support window accepted',
    description: 'Someone explicitly accepted a request and agreed a response window.',
    required: ['supportId', 'windowStart', 'windowEnd'],
    optional: ['note'],
    needsProject: false,
    stewardOnly: false,
    reference: 'supportId',
  },
  support_response: {
    label: 'Support response',
    description: 'A linked response exists. Whether it helped is recorded separately.',
    required: ['supportId', 'note'],
    optional: [],
    needsProject: false,
    stewardOnly: false,
    reference: 'supportId',
  },
  useful_help_acknowledged: {
    label: 'Useful-help acknowledgement',
    description: 'The recipient says whether the help was useful. Not answering is not the same as unhelpful.',
    required: ['supportId', 'outcome'],
    optional: ['note'],
    needsProject: false,
    stewardOnly: false,
    reference: 'supportId',
  },
  project_update: {
    label: 'Project update',
    description: 'A reported project stage. An update time is not a ship date.',
    required: ['stage', 'note'],
    optional: ['artifactUrl'],
    needsProject: true,
    stewardOnly: false,
  },
  retrospective_action: {
    label: 'Retrospective action',
    description: 'An action agreed in a retrospective. A due date is not completion.',
    required: ['actionId', 'note'],
    optional: [],
    needsProject: true,
    stewardOnly: false,
    reference: 'actionId',
    root: true,
  },
  action_outcome: {
    label: 'Action outcome',
    description: 'What happened to a retrospective action, including “not known yet”.',
    required: ['actionId', 'outcome', 'note'],
    optional: [],
    needsProject: true,
    stewardOnly: false,
    reference: 'actionId',
  },
  learning_report: {
    label: 'Learning report',
    description: 'A voluntary self-report of what changed. Not an assessed skill.',
    required: ['note', 'outcome'],
    optional: [],
    needsProject: false,
    stewardOnly: false,
  },
  beneficiary_feedback: {
    label: 'Beneficiary feedback',
    description: 'Explicit feedback from someone the work was meant to help.',
    required: ['note', 'outcome'],
    optional: [],
    needsProject: false,
    stewardOnly: false,
  },
  stewardship_effort: {
    label: 'Stewardship effort',
    description: 'Voluntarily recorded minutes. Never inferred from message times.',
    required: ['minutes', 'note'],
    optional: [],
    needsProject: false,
    stewardOnly: false,
  },
};

export const eventOrder = Object.keys(eventSpecs) as OperationalEventType[];

export const fieldLabel: Record<EventField, string> = {
  supportId: 'Support request',
  actionId: 'Retrospective action',
  windowStart: 'Window opens',
  windowEnd: 'Window closes',
  outcome: 'Outcome',
  stage: 'Stage',
  artifactUrl: 'Artifact link (optional)',
  minutes: 'Minutes',
  note: 'Note',
};

export const stages: ProjectStage[] = ['idea', 'planning', 'building', 'testing', 'shipped', 'paused', 'unknown'];

export type EventDraft = Partial<Record<EventField, string>>;

/** A fresh opaque chain reference for a new request or action. */
export function newReference(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Converts a `datetime-local` value (interpreted as UTC) to ISO 8601 with offset. */
export function localToIso(value: string | undefined): string | null {
  if (value === undefined || value.trim() === '') return null;
  const date = new Date(`${value}${value.length === 16 ? ':00' : ''}Z`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export interface PayloadResult {
  payload: EventPayload | null;
  errors: Partial<Record<EventField, string>>;
}

/** Builds exactly the payload keys the strict schema accepts for this event type, or explains what is missing. */
export function buildPayload(type: OperationalEventType, draft: EventDraft): PayloadResult {
  const spec = eventSpecs[type];
  const errors: Partial<Record<EventField, string>> = {};
  const payload: EventPayload = {};
  for (const field of [...spec.required, ...spec.optional]) {
    const raw = (draft[field] ?? '').trim();
    const required = spec.required.includes(field);
    if (raw === '') {
      if (field === 'note' && required) payload.note = '';
      else if (field === 'supportId' || field === 'actionId') errors[field] = `Choose the ${fieldLabel[field].toLowerCase()} this follows.`;
      else if (required) errors[field] = `${fieldLabel[field]} is required.`;
      continue;
    }
    switch (field) {
      case 'windowStart':
      case 'windowEnd': {
        const iso = localToIso(raw);
        if (iso === null) errors[field] = 'Enter a valid date and time.';
        else payload[field] = iso;
        break;
      }
      case 'minutes': {
        const minutes = Number(raw);
        if (!Number.isFinite(minutes) || minutes < 0 || minutes > 1440) errors.minutes = 'Enter between 0 and 1440 minutes.';
        else payload.minutes = minutes;
        break;
      }
      case 'outcome':
        payload.outcome = raw as Outcome;
        break;
      case 'stage':
        payload.stage = raw as ProjectStage;
        break;
      case 'artifactUrl':
        try {
          const url = new URL(raw);
          if (url.protocol !== 'https:' && url.protocol !== 'http:') throw new Error('protocol');
          payload.artifactUrl = raw;
        } catch {
          errors.artifactUrl = 'Enter a full http(s) link.';
        }
        break;
      default:
        payload[field] = raw;
    }
  }
  if (payload.windowStart !== undefined && payload.windowEnd !== undefined && payload.windowEnd < payload.windowStart) {
    errors.windowEnd = 'The window must close after it opens.';
  }
  return { payload: Object.keys(errors).length === 0 ? payload : null, errors };
}
